import { format } from 'date-fns';
import { isArray } from 'lodash';

import { DOCUMENT_NAME_TO_ID, SHARED_DOCUMENTS } from 'global/SharedDocumentFields';
import { STATIC_FIELDS } from 'global/constants';

export const getFilesKeys = (values) =>
  Object.keys(values).filter((key) => !STATIC_FIELDS.includes(key));

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const prepFilesForUpload = async (files) =>
  Promise.all(
    Array.from(files).map(async (file) => ({
      name: file.name,
      contentType: file.type ? file.type : null,
      base64: await toBase64(file),
      size: file.size,
    }))
  );

export const prepDocumentForMutation = (document) => ({
  contentType: document.contentType,
  base64: document.base64.split(',')[1],
  fileName: document.name,
});

export const downloadPdfFromBase64 = (base64String, fileName) => {
  // Convert base64 string to a Blob
  const binaryString = window.atob(base64String);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });

  // Create an object URL from the Blob
  const url = window.URL.createObjectURL(blob);

  // Create a link element and start the download
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  // Release the object URL
  window.URL.revokeObjectURL(url);
};

export const calculateTotalFilesSize = (values) => {
  const filesFields = getFilesKeys(values);
  let totalSize = 0;
  filesFields.forEach((key) => {
    if (isArray(values[key])) {
      values[key].forEach((file) => {
        totalSize += file.size;
      });
    } else {
      Object.keys(values[key]).forEach((internalKey) => {
        values[key][internalKey].forEach((file) => {
          totalSize += file.size;
        });
      });
    }
  });
  return totalSize;
};

export const createDocumentName = (installer, documentBaseName, expert) =>
  documentBaseName
    .split('_')
    .map((nameSection) => {
      switch (nameSection) {
        case 'Date':
          return format(new Date(), 'yyyyMMdd');
        case 'installateurnummer':
          return installer.id;
        case 'firstname':
          return expert.firstName;
        case 'lastname':
          return expert.lastName;
        default:
          return nameSection;
      }
    })
    .join('_');

const generateUploadName = (installer, baseUploadName, expert, index, total) => {
  let uploadName = createDocumentName(installer, baseUploadName, expert);
  if (total > 1) {
    uploadName += `_${String(index + 1).padStart(2, '0')}`;
  }
  return uploadName;
};

export const getFileWithNames = (values, valuesFirstPage, installer) => {
  const filesKey = getFilesKeys(values);

  const fileWithNames = filesKey
    .map((key) => {
      if (isArray(values[key])) {
        return values[key].map((file, i) => {
          const expert = valuesFirstPage.experts.find(
            (expertFilter) => expertFilter.id.toString() === key
          );
          const baseUploadName = SHARED_DOCUMENTS[DOCUMENT_NAME_TO_ID[key]].uploadName;
          const uploadName = generateUploadName(
            installer,
            baseUploadName,
            expert,
            i,
            values[key].length
          );
          return { ...file, name: uploadName };
        });
      }

      return Object.keys(values[key])
        .map((documentKey) =>
          values[key][documentKey].map((file, i) => {
            const expert = valuesFirstPage.experts.find(
              (expertFilter) => expertFilter.id.toString() === key
            );
            const baseUploadName = SHARED_DOCUMENTS[DOCUMENT_NAME_TO_ID[documentKey]].uploadName;
            const uploadName = generateUploadName(
              installer,
              baseUploadName,
              expert,
              i,
              values[key][documentKey].length
            );
            return { ...file, name: uploadName };
          })
        )
        .flat(1);
    })
    .flat(1);

  return fileWithNames;
};
